import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import {
  clearMessages,
  selectIsLoading,
} from "../features/msp/slices/apiStatusSlice";
import Loading from "../assets/images/loading.gif";
import { SuccessIcon, WarningIcon } from "../features/common/icons";

export default function NotificationsOverlay() {
  const { error, success } = useAppSelector((s) => s.apiStatus);
  const loading = useAppSelector(selectIsLoading);
  const dispatch = useAppDispatch();

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  // progress width for success/error
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const shouldShow = !!(loading || error || success);
    if (shouldShow) {
      setMounted(true);
      requestAnimationFrame(() => setVisible(true));
    } else if (mounted) {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [loading, error, success, mounted]);

  // success auto-dismiss with animated progress
  useEffect(() => {
    if (success || error) {
      setProgress(100);
      const duration = 3000; // ms
      const interval = 30;
      let elapsed = 0;

      const id = setInterval(() => {
        elapsed += interval;
        const pct = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(pct);
        if (elapsed >= duration) {
          clearInterval(id);
          if (success) dispatch(clearMessages());
          // errors you can keep until dismiss, or auto-clear if you want
        }
      }, interval);

      return () => clearInterval(id);
    }
  }, [success, error, dispatch]);

  if (!mounted) return null;

  const content = (
    <div
      aria-live="polite"
      role="status"
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      {/* backdrop only for loading */}
      {loading && (
        <div
          className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
            visible ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden
        />
      )}

      {/* centered loading indicator */}
      {loading && (
        <div className="relative z-10 flex flex-col items-center gap-4">
          <img src={Loading} alt="loading" className="w-28 h-28" aria-hidden />
          <p className="text-white text-lg font-medium">Loading...</p>
        </div>
      )}

      {/* top notification bar */}
      {(error || success) && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-xl">
          <div className="relative bg-white rounded-md shadow-md px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* status icon */}
              <div
                className="flex items-center justify-center w-6 h-6 rounded-full"
                style={{
                  backgroundColor: error ? "#FC0005" : "#42CC45",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                {error ? <WarningIcon /> : <SuccessIcon />}
              </div>
              <p
                className="font-medium"
                style={{
                  color: "#7C7C7C",
                  fontSize: 18,
                  fontStyle: "normal",
                  fontWeight: 400,
                }}
              >
                {error || success}
              </p>
            </div>
            <button
              className="ml-4 text-sm text-gray-500 hover:text-gray-800 cursor-pointer"
              onClick={() => dispatch(clearMessages())}
            >
              ✕
            </button>

            {/* solid color status bar at bottom */}
            {/* regress bar at bottom */}
            <div
              className="absolute left-0 bottom-0 h-1 w-full rounded-b-md"
              style={{
                backgroundColor: error ? "#FDCBCB" : "#DFF6E0", // trail color
              }}
            >
              <div
                className="h-1 rounded-b-md"
                style={{
                  width: `${progress}%`,
                  transition: "width 0.03s linear",
                  backgroundColor: error ? "#FF0000" : "#42CC45", // main bar color
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return typeof document !== "undefined"
    ? ReactDOM.createPortal(content, document.body)
    : null;
}
